// src/components/pages/application/LicenseBreachBanner.js
//
// Licensing v2 Phase 3 — tenant-facing banner.
//
// Polls /api/user/v1/licensing/status every 5 minutes and renders:
//   - nothing             if no active breaches
//   - WARNING bar (yellow) if the tenant has soft (active) breach(es)
//   - ERROR bar (red)      if any active breach has resolution_state =
//                          resolved_hard_cap (i.e. SKU is suspended).
//                          NB: hard-capped SKUs flip expiry_date in PG
//                          but we still surface them here until the
//                          super-admin extends or revokes.
//
// "Request additional quota" button POSTs to
// /api/user/v1/licensing/request-quota with the first active breach_id.
// 409 conflicts (already pending) surface as a neutral info toast.

import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Box, Stack, Typography, Button, Collapse } from "@mui/material";
import { TriangleAlert, OctagonX } from "lucide-react";

import userAxios from "../../../utils/axios/v1/userAxios";
import { useNotification } from "../../../contexts/NotificationContext";

const POLL_MS = 5 * 60 * 1000;

const ERROR_ICON_COLOR = "#d04340";
const WARNING_ICON_COLOR = "#d97706";

function pickSeverity(breach, skus) {
  // A breach row is "soft" while resolutionState=active, "hard" once it
  // becomes resolved_hard_cap. Phase 3 status endpoint returns active
  // breaches only — the hard-capped SKU shows up via skus[].expired.
  // Treat: any hard-capped sku found in skus -> red.
  if (breach && breach.resolution_state === "resolved_hard_cap") return "error";
  const sku = skus.find((s) => s.sku_id === breach.sku_id);
  if (sku && sku.expired) return "error";
  return "warning";
}

function daysFromNow(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86400000));
}

function buildBreachMessage(breach, skus) {
  const isHard = pickSeverity(breach, skus) === "error";
  if (isHard) {
    return `Your '${breach.sku_name}' license has been suspended due to quota overage. Some features are disabled.`;
  }
  const daysLeft = daysFromNow(breach.hard_cap_at);
  return `Your '${breach.sku_name}' license has exceeded its ${breach.dimension} quota. ${daysLeft != null ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} until the license is suspended.` : ""}`;
}

function BreachMessageRow({ breach, skus, palette, children }) {
  const isHard = pickSeverity(breach, skus) === "error";
  const RowIcon = isHard ? OctagonX : TriangleAlert;
  const iconColor = isHard ? ERROR_ICON_COLOR : (palette.icon || WARNING_ICON_COLOR);

  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        <RowIcon size={20} color={iconColor} aria-hidden />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          component="div"
          sx={{ color: palette.fg, fontWeight: 500 }}
        >
          {buildBreachMessage(breach, skus)}
          {children}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function LicenseBreachBanner() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { showSuccess, showInfo, showError } = useNotification();

  const fetchStatus = useCallback(async () => {
    try {
      const r = await userAxios.get("/licensing/status");
      setStatus(r && r.data ? r.data : null);
    } catch (err) {
      // 401/403 means the user lacks PAYMENT permission OR is not in
      // an org context — banner stays hidden. Other errors are silent
      // because this is decorative; the daemon notifications carry the
      // authoritative message.
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const firstBreach = useMemo(() => {
    if (!status || !Array.isArray(status.active_breaches)) return null;
    return status.active_breaches[0] || null;
  }, [status]);

  const extraBreaches = useMemo(() => {
    if (!status || !Array.isArray(status.active_breaches)) return [];
    return status.active_breaches.slice(1);
  }, [status]);

  useEffect(() => {
    setExpanded(false);
  }, [status?.active_breaches?.length]);

  if (!firstBreach) return null;

  const skus = status.skus || [];
  const severity = pickSeverity(firstBreach, skus);
  const isHard = severity === "error";

  const palette = isHard
    ? { bg: "#fdecea", border: "#f5c2c0", fg: "#7d1f1d", icon: ERROR_ICON_COLOR }
    : { bg: "#fff4e5", border: "#f5d6a8", fg: "#7a4a05", icon: WARNING_ICON_COLOR };

  const extraCount = extraBreaches.length;

  const onRequest = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await userAxios.post("/licensing/request-quota", {
        breach_id: firstBreach.id,
      });
      const msg = (r && r.data && r.data.message) || "Request sent to your account manager.";
      showSuccess(msg, "Request sent");
      // Refresh status so the banner reflects the new requestPending state
      fetchStatus();
    } catch (err) {
      const status = err && err.response && err.response.status;
      const apiMsg = err && err.response && err.response.data && (err.response.data.message || err.response.data.msg);
      if (status === 409) {
        showInfo(apiMsg || "A request is already pending review.", "Already requested");
      } else {
        showError(apiMsg || "Failed to send request.", "Request failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const moreToggle = extraCount > 0 ? (
    <>
      {" "}
      <Button
        variant="text"
        size="small"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="license-breach-banner-more"
        sx={{
          minWidth: 0,
          p: 0,
          verticalAlign: "baseline",
          fontSize: "inherit",
          fontWeight: "inherit",
          lineHeight: "inherit",
          color: palette.fg,
          textDecoration: "underline",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "transparent",
            color: palette.icon,
          },
        }}
      >
        {expanded ? "show less" : `(+${extraCount} more)`}
      </Button>
    </>
  ) : null;

  return (
    <Box
      sx={{
        backgroundColor: palette.bg,
        borderBottom: `1px solid ${palette.border}`,
        color: palette.fg,
        px: 3,
        py: 1.5,
      }}
      role="alert"
      aria-live="polite"
      data-testid="license-breach-banner"
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
      >
        <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
          <BreachMessageRow breach={firstBreach} skus={skus} palette={palette}>
            {moreToggle}
          </BreachMessageRow>
          <Collapse in={expanded && extraCount > 0}>
            <Stack spacing={1}>
              {extraBreaches.map((breach) => (
                <BreachMessageRow
                  key={breach.id}
                  breach={breach}
                  skus={skus}
                  palette={palette}
                />
              ))}
            </Stack>
          </Collapse>
        </Stack>
        <Button
          onClick={onRequest}
          disabled={busy || firstBreach.request_pending}
          size="small"
          variant="contained"
          sx={{
            backgroundColor: palette.fg,
            color: "white",
            "&:hover": { backgroundColor: palette.icon },
            textTransform: "none",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
          data-testid="license-breach-banner-cta"
        >
          {firstBreach.request_pending ? "Request pending" : busy ? "Sending..." : "Request additional quota"}
        </Button>
      </Stack>
    </Box>
  );
}
